import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrophone,
  faFilePdf,
  faImage,
  faVolumeUp,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const SheEarnsVoiceBook = () => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();

  const [transcribedText, setTranscribedText] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [interimText, setInterimText] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en-US");
  const recognitionRef = useRef(null);
  const canvasRef = useRef(null);

  const fullText = (transcribedText + (interimText ? " " + interimText : "")).trim();

  const languages = [
    { code: "en-US", name: "English (US)" },
    { code: "es-ES", name: "Español (España)" },
    { code: "fr-FR", name: "Français (France)" },
    { code: "de-DE", name: "Deutsch (Deutschland)" },
    { code: "it-IT", name: "Italiano (Italia)" },
    { code: "pt-BR", name: "Português (Brasil)" },
    { code: "hi-IN", name: "हिन्दी (India)" },
    { code: "zh-CN", name: "中文 (中国)" },
    { code: "ar-SA", name: "العربية (السعودية)" },
    { code: "ru-RU", name: "Русский (Россия)" },
    { code: "ja-JP", name: "日本語 (日本)" },
    { code: "ko-KR", name: "한국어 (한국)" },
    { code: "nl-NL", name: "Nederlands (Nederland)" },
    { code: "tr-TR", name: "Türkçe (Türkiye)" },
  ];

  const downloadAsPDF = async () => {
    if (!canvasRef.current || !transcribedText.trim()) return;

    const canvas = await html2canvas(canvasRef.current, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = -heightLeft + 10;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save("voice-book.pdf");
  };

  const downloadAsImage = async () => {
    if (!canvasRef.current || !transcribedText.trim()) return;
    const canvas = await html2canvas(canvasRef.current, { scale: 2 });
    const link = document.createElement("a");
    link.download = "voice-book.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const readBack = () => {
    if ("speechSynthesis" in window && fullText) {
      const utterance = new SpeechSynthesisUtterance(fullText);
      utterance.lang = selectedLanguage;
      utterance.rate = 0.9;

      // Cancel any ongoing speech
      window.speechSynthesis.cancel();

      // Wait for voices to load before assigning
      const assignVoice = () => {
        const voices = window.speechSynthesis.getVoices();
        const match = voices.find((v) =>
          v.lang.startsWith(selectedLanguage.split("-")[0])
        );
        if (match) utterance.voice = match;
        window.speechSynthesis.speak(utterance);
      };

      if (window.speechSynthesis.getVoices().length === 0) {
        window.speechSynthesis.onvoiceschanged = assignVoice;
      } else assignVoice();
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
    setInterimText("");
  };

  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech recognition not supported. Please use Chrome or Edge.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = selectedLanguage;

    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      let interimTranscript = "";
      let finalTranscript = transcribedText;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript.toLowerCase().trim();
        if (event.results[i].isFinal) {
          if (commands[transcript]) {
            commands[transcript]();
          } else {
            finalTranscript += transcript + " ";
          }
        } else {
          interimTranscript += transcript;
        }
      }

      setInterimText(interimTranscript);
      setTranscribedText(finalTranscript.trim());
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      stopListening();
    };

    recognition.onend = () => stopListening();
    recognition.start();
  };

  const handleVoiceToggle = () => (isListening ? stopListening() : startListening());
  const handleBackToHome = () => navigate(-1);

  // Command Map (must come after function declarations)
  const commands = {
    "download pdf": downloadAsPDF,
    "download image": downloadAsImage,
    "read back": readBack,
    start: startListening,
    stop: stopListening,
  };

  useEffect(() => {
    return () => stopListening(); // cleanup on unmount
  }, []);

  return (
    <div className="min-h-screen bg-black flex flex-col justify-between">
      <div className="text-center py-4">
        <h1 className="bg-pink-600 text-white text-lg font-semibold rounded-lg inline-block px-6 py-2 shadow-lg">
          SAKSHAM Shakti Voice Book
        </h1>
      </div>

      <div className="flex flex-col items-center justify-center px-4 flex-1">
        <div className="mb-4 w-ful">
          <label className="block text-sm font-medium text-white mb-2">
            Select Language :
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="h-10 px-3 py-2 border text-black border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-pink-500"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
           </label>
        </div>

        <div
          ref={canvasRef}
          className="w-full max-w-4xl h-80 bg-white border-2 border-gray-300 rounded-lg p-4 overflow-y-auto shadow-lg text-gray-800"
          contentEditable={!isListening}
          suppressContentEditableWarning={true}
          onInput={(e) => setTranscribedText(e.currentTarget.innerText)}
        >
          {fullText ||
            `Start speaking in ${
              languages.find((l) => l.code === selectedLanguage)?.name || "selected language"
            } or say "read back", "download pdf", "stop"...`}
        </div>

        <div className="grid grid-cols-3 lg:grid-cols-3 justify-center gap-4 mt-2 place-items-center">
          <button
            onClick={handleVoiceToggle}
            className={`p-2 w-20 rounded-full shadow-lg ${
              isListening
                ? "bg-red-500 text-white"
                : "bg-pink-600 text-white hover:bg-pink-700"
            }`}
            title={isListening ? "Stop Listening" : "Start Listening"}
          >
            <FontAwesomeIcon icon={faMicrophone} size="2x" />
          </button>

          <button
            onClick={readBack}
            className="bg-purple-600 text-white px-2 py-2 rounded-lg shadow-lg hover:bg-purple-700 flex items-center space-x-2"
            disabled={!fullText}
          >
            <FontAwesomeIcon icon={faVolumeUp} />
          </button>

          <button
            onClick={downloadAsPDF}
            className="bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-green-700 flex items-center space-x-2"
            disabled={!transcribedText.trim()}
          >
            <FontAwesomeIcon icon={faFilePdf} />
          </button>
        </div>
        <button
          className="bg-pink-600 text-white px-6 py-2 rounded-lg shadow-lg hover:bg-pink-700"
          onClick={handleBackToHome}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default SheEarnsVoiceBook;
